import { VOICE_PARTS } from '@corocanto/scoring';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError } from '../api/client';
import {
  type EventDetail,
  type EventDetails,
  type EventRequest,
  EVENT_TYPES,
  type EventType,
  type TypeInfo,
  useDeleteEvent,
  useEvent,
  useEventTypes,
  useSaveEvent,
  useSetLineup,
} from '../api/events';
import { useMembers } from '../api/songs';
import type { Member } from '../api/types';
import { useMembership } from '../auth/session';
import { Person } from '../components/Avatar';
import { Dialog } from '../components/Dialog';
import { ErrorView, Loading } from '../components/Guards';
import ui from '../styles/ui.module.css';
import styles from './EventFormPage.module.css';

interface FormState {
  type: EventType;
  name: string;
  startsOn: string;
  endsOn: string;
  venue: string;
  callAndDress: string;
  replyDeadline: string;
  readinessTracking: boolean;
  limitedLineup: boolean;
  details: Record<string, string>;
  lineup: ReadonlySet<string>;
}

function fromEvent(event: EventDetail): FormState {
  return {
    type: event.type,
    name: event.name,
    startsOn: event.startsOn,
    endsOn: event.endsOn ?? '',
    venue: event.venue ?? '',
    callAndDress: event.callAndDress ?? '',
    replyDeadline: event.replyDeadline ?? '',
    readinessTracking: event.readinessTracking,
    limitedLineup: event.limitedLineup,
    details: Object.fromEntries(Object.entries(event.details).map(([k, v]) => [k, String(v)])),
    lineup: new Set(event.limitedLineup ? event.members.filter((m) => m.inLineup).map((m) => m.userId) : []),
  };
}

/** Director: create an event, or edit one (wireframe 08's form). */
export function EventFormPage() {
  const { eventId } = useParams();
  const membership = useMembership();
  const types = useEventTypes();
  const event = useEvent(membership?.choirId, eventId);
  const members = useMembers(membership?.choirId);

  if (!membership) return null;
  if (types.isPending || members.isPending || (eventId && event.isPending)) return <Loading />;
  if (types.isError || members.isError || !types.data || !members.data) return <ErrorView onRetry={() => void types.refetch()} />;
  if (eventId && (event.isError || !event.data)) return <ErrorView onRetry={() => void event.refetch()} />;
  return (
    <EventForm
      key={eventId ?? 'new'}
      choirId={membership.choirId}
      types={types.data}
      singers={members.data.filter((m) => m.roles.includes('CHORIST'))}
      event={event.data ?? null}
    />
  );
}

function EventForm({ choirId, types, singers, event }: { choirId: string; types: TypeInfo[]; singers: Member[]; event: EventDetail | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const save = useSaveEvent(choirId);
  const setLineup = useSetLineup(choirId);
  const remove = useDeleteEvent(choirId);
  const typeInfo = (type: EventType) => types.find((x) => x.type === type);
  const [form, setForm] = useState<FormState>(() => {
    if (event) return fromEvent(event);
    const concert = typeInfo('CONCERT');
    return {
      type: 'CONCERT',
      name: '',
      startsOn: '',
      endsOn: '',
      venue: '',
      callAndDress: '',
      replyDeadline: '',
      readinessTracking: concert?.readinessTracking ?? true,
      limitedLineup: concert?.limitedLineup ?? false,
      details: {},
      lineup: new Set(),
    };
  });
  const [typeChosen, setTypeChosen] = useState(false);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const [lineupOpen, setLineupOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const info = typeInfo(form.type);
  const update = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));

  function chooseType(type: EventType) {
    const next = typeInfo(type);
    const keys = new Set(next?.fields.map((f) => f.key));
    setForm((f) => ({
      ...f,
      type,
      readinessTracking: next?.readinessTracking ?? f.readinessTracking,
      limitedLineup: next?.limitedLineup ?? f.limitedLineup,
      details: Object.fromEntries(Object.entries(f.details).filter(([k]) => keys.has(k))),
    }));
    setTypeChosen(true);
  }

  async function submit() {
    setError(null);
    const details: EventDetails = {};
    for (const field of info?.fields ?? []) {
      const value = form.details[field.key]?.trim();
      if (!value) continue;
      details[field.key] = field.kind === 'MINUTES' ? Number(value) : value;
    }
    const request: EventRequest = {
      type: form.type,
      name: form.name.trim(),
      startsOn: form.startsOn,
      endsOn: form.endsOn || null,
      venue: form.venue.trim() || null,
      callAndDress: form.callAndDress.trim() || null,
      readinessTracking: form.readinessTracking,
      limitedLineup: form.limitedLineup,
      replyDeadline: form.replyDeadline || null,
      details,
    };
    try {
      const saved = await save.mutateAsync({ id: event?.id, request });
      if (form.limitedLineup) await setLineup.mutateAsync({ eventId: saved.id, userIds: [...form.lineup] });
      navigate(!event && saved.readinessTracking ? `/events/${saved.id}/repertoire` : `/events/${saved.id}`);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : undefined;
      setError(t(`apiErrors.${code ?? 'unknown'}`, { defaultValue: t('apiErrors.unknown') }));
    }
  }

  const busy = save.isPending || setLineup.isPending;
  const canSave = !!form.name.trim() && !!form.startsOn && !busy;

  return (
    <div className={ui.page}>
      <Link to={event ? `/events/${event.id}` : '/events'} className={styles.back}>
        {event ? event.name : t('eventPage.back')}
      </Link>
      <section className={`${ui.card} ${styles.form}`} data-testid="event-form">
        <div className={styles.header}>
          <h1 className={styles.title}>{event ? t('eventForm.editTitle') : t('eventForm.newTitle')}</h1>
          <button type="button" className={ui.button} onClick={() => setDefaultsOpen(true)}>
            ⓘ {t('eventForm.defaults')}
          </button>
        </div>

        <div className={ui.field}>
          <span className={ui.label} id="type-label">
            {t('eventForm.type')}
          </span>
          <div className={styles.types} role="radiogroup" aria-labelledby="type-label">
            {EVENT_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                role="radio"
                aria-checked={form.type === type}
                className={form.type === type ? `${styles.type} ${styles.typeOn}` : styles.type}
                onClick={() => chooseType(type)}
              >
                {t(`eventTypes.${type}`)}
              </button>
            ))}
          </div>
          {typeChosen && (
            <span className={ui.muted} data-testid="type-hint">
              {t('eventForm.typeHint', {
                type: t(`eventTypes.${form.type}`),
                lineup: form.limitedLineup ? t('eventForm.hintLimited') : t('eventForm.hintWhole'),
                tracking: form.readinessTracking ? t('eventForm.hintOn') : t('eventForm.hintOff'),
              })}
            </span>
          )}
        </div>

        <div className={styles.grid}>
          <TextField label={t('eventForm.name')} value={form.name} onChange={(name) => update({ name })} required />
          <div className={styles.dates}>
            <DateField label={t('eventForm.startsOn')} value={form.startsOn} onChange={(startsOn) => update({ startsOn })} required />
            <DateField label={t('eventForm.endsOn')} value={form.endsOn} onChange={(endsOn) => update({ endsOn })} />
          </div>
          <TextField label={t('eventForm.venue')} value={form.venue} onChange={(venue) => update({ venue })} />
          <TextField label={t('eventForm.callAndDress')} value={form.callAndDress} onChange={(callAndDress) => update({ callAndDress })} />
          <DateField label={t('eventForm.replyDeadline')} value={form.replyDeadline} onChange={(replyDeadline) => update({ replyDeadline })} />
        </div>

        <fieldset className={styles.lineup}>
          <legend className={ui.label}>{t('eventForm.lineup')}</legend>
          <label className={styles.radio}>
            <input type="radio" name="lineup" checked={!form.limitedLineup} onChange={() => update({ limitedLineup: false })} />
            {t('eventForm.wholeChoir')}
          </label>
          <label className={styles.radio}>
            <input type="radio" name="lineup" checked={form.limitedLineup} onChange={() => update({ limitedLineup: true })} />
            {t('eventForm.limited')}
          </label>
          {form.limitedLineup && (
            <button type="button" className={ui.linkButton} onClick={() => setLineupOpen(true)}>
              {t('eventForm.chooseLineup', { count: form.lineup.size })}
            </button>
          )}
        </fieldset>

        <label className={styles.check}>
          <input
            type="checkbox"
            checked={form.readinessTracking}
            onChange={(e) => update({ readinessTracking: e.target.checked })}
          />
          {t('eventForm.tracking')} <span className={ui.muted}>· {t('eventForm.trackingHint')}</span>
        </label>

        {info && info.fields.length > 0 && (
          <div className={styles.extras} data-testid="type-fields">
            <strong>{t(`eventForm.typeSection.${form.type}`)}</strong>
            <div className={styles.grid}>
              {info.fields.map((field) =>
                field.kind === 'DATE' ? (
                  <DateField
                    key={field.key}
                    label={t(`eventForm.fields.${field.key}`)}
                    value={form.details[field.key] ?? ''}
                    onChange={(value) => update({ details: { ...form.details, [field.key]: value } })}
                  />
                ) : (
                  <TextField
                    key={field.key}
                    label={t(`eventForm.fields.${field.key}`)}
                    value={form.details[field.key] ?? ''}
                    numeric={field.kind === 'MINUTES'}
                    onChange={(value) => update({ details: { ...form.details, [field.key]: value } })}
                  />
                ),
              )}
            </div>
          </div>
        )}

        {error && (
          <div className={`${ui.notice} ${ui.noticeError}`} role="alert">
            {error}
          </div>
        )}
        <div className={styles.buttons}>
          {event && (
            <button type="button" className={`${ui.button} ${styles.delete}`} onClick={() => setDeleteOpen(true)}>
              {t('eventForm.delete')}
            </button>
          )}
          <span className={styles.spacer} />
          <Link to={event ? `/events/${event.id}` : '/events'} className={ui.button}>
            {t('eventForm.cancel')}
          </Link>
          <button type="button" className={`${ui.button} ${ui.primary}`} disabled={!canSave} onClick={() => void submit()}>
            {!event && form.readinessTracking ? t('eventForm.saveAndRepertoire') : t('eventForm.save')}
          </button>
        </div>
      </section>

      <TypeDefaultsDialog open={defaultsOpen} types={types} onClose={() => setDefaultsOpen(false)} />
      <LineupDialog
        open={lineupOpen}
        singers={singers}
        selected={form.lineup}
        onChange={(lineup) => update({ lineup })}
        onClose={() => setLineupOpen(false)}
      />
      {event && (
        <Dialog open={deleteOpen} title={t('eventForm.deleteTitle')} onClose={() => setDeleteOpen(false)}>
          <p className={styles.noMargin}>{t('eventForm.deleteQuestion', { name: event.name })}</p>
          <div className={styles.buttons}>
            <span className={styles.spacer} />
            <button type="button" className={ui.button} onClick={() => setDeleteOpen(false)}>
              {t('eventForm.cancel')}
            </button>
            <button
              type="button"
              className={`${ui.button} ${styles.deleteConfirm}`}
              disabled={remove.isPending}
              onClick={() => remove.mutate(event.id, { onSuccess: () => navigate('/events') })}
            >
              {t('eventForm.deleteConfirm')}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
}

function TextField({ label, value, onChange, required, numeric }: { label: string; value: string; onChange: (v: string) => void; required?: boolean; numeric?: boolean }) {
  return (
    <label className={ui.field}>
      <span className={ui.label}>{label}</span>
      <input
        className={ui.input}
        value={value}
        maxLength={numeric ? 3 : 300}
        inputMode={numeric ? 'numeric' : undefined}
        required={required}
        onChange={(e) => onChange(numeric ? e.target.value.replace(/\D/g, '') : e.target.value)}
      />
    </label>
  );
}

function DateField({ label, value, onChange, required }: { label: string; value: string; onChange: (v: string) => void; required?: boolean }) {
  return (
    <label className={ui.field}>
      <span className={ui.label}>{label}</span>
      <input className={ui.input} type="date" value={value} required={required} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

/** Wireframe 09: what each type brings when it is chosen. */
function TypeDefaultsDialog({ open, types, onClose }: { open: boolean; types: TypeInfo[]; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} title={t('eventForm.defaults')} onClose={onClose} data-testid="type-defaults" wide>
      <p className={`${styles.noMargin} ${ui.muted}`}>{t('typeDefaults.intro')}</p>
      <table className={ui.table}>
        <thead>
          <tr>
            <th>{t('typeDefaults.colType')}</th>
            <th>{t('typeDefaults.colTracking')}</th>
            <th>{t('typeDefaults.colLineup')}</th>
            <th>{t('typeDefaults.colExtras')}</th>
          </tr>
        </thead>
        <tbody>
          {types.map((info) => (
            <tr key={info.type}>
              <td>
                <strong>{t(`eventTypes.${info.type}`)}</strong>
              </td>
              <td>{t(`typeDefaults.trackingNote.${info.type}`, { defaultValue: info.readinessTracking ? t('typeDefaults.on') : t('typeDefaults.off') })}</td>
              <td>{t(`typeDefaults.lineupNote.${info.type}`, { defaultValue: info.limitedLineup ? t('eventForm.limited') : t('eventForm.wholeChoir') })}</td>
              <td className={ui.muted}>{t(`typeDefaults.extras.${info.type}`)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className={styles.buttons}>
        <span className={styles.spacer} />
        <button type="button" className={`${ui.button} ${ui.primary}`} onClick={onClose}>
          {t('typeDefaults.ok')}
        </button>
      </div>
    </Dialog>
  );
}

/** Tick the singers of a limited line-up, grouped by section. */
function LineupDialog({
  open,
  singers,
  selected,
  onChange,
  onClose,
}: {
  open: boolean;
  singers: Member[];
  selected: ReadonlySet<string>;
  onChange: (lineup: ReadonlySet<string>) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toggle = (id: string, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(id);
    else next.delete(id);
    onChange(next);
  };
  const sections = [...VOICE_PARTS, null] as const;
  return (
    <Dialog open={open} title={t('lineupDialog.title')} onClose={onClose} data-testid="lineup-dialog">
      <p className={`${styles.noMargin} ${ui.muted}`}>{t('lineupDialog.intro')}</p>
      <div className={styles.lineupTools}>
        <strong>{t('lineupDialog.count', { count: selected.size })}</strong>
        <button type="button" className={ui.linkButton} onClick={() => onChange(new Set(singers.map((s) => s.userId)))}>
          {t('lineupDialog.all')}
        </button>
        <button type="button" className={ui.linkButton} onClick={() => onChange(new Set())}>
          {t('lineupDialog.none')}
        </button>
      </div>
      <div className={styles.sections}>
        {sections.map((part) => {
          const group = singers.filter((s) => s.defaultPart === part);
          if (group.length === 0) return null;
          return (
            <div key={part ?? 'none'}>
              <div className={ui.label}>{part ? t(`parts.${part}`) : '–'}</div>
              {group.map((s) => (
                <label key={s.userId} className={styles.member}>
                  <input type="checkbox" checked={selected.has(s.userId)} onChange={(e) => toggle(s.userId, e.target.checked)} />
                  <Person name={s.displayName} leadsSection={s.leadsSection} size="small">
                    {s.displayName}
                  </Person>
                </label>
              ))}
            </div>
          );
        })}
      </div>
      <div className={styles.buttons}>
        <span className={styles.spacer} />
        <button type="button" className={`${ui.button} ${ui.primary}`} onClick={onClose}>
          {t('lineupDialog.done')}
        </button>
      </div>
    </Dialog>
  );
}
